import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, ListMusic, Shuffle } from "lucide-react";
import { API } from "@/lib/api";

const base = API.replace(/\/api$/, "");

// Sequential player for an artist's tracks ("Play all"): sticky bar with prev/next, shuffle, progress.
export default function ArtistPlaylist({ tracks, name, onStart }) {
  const audio = useRef(null);
  const [idx, setIdx] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [shuffle, setShuffle] = useState(false);
  const started = useRef(false);
  const order = useRef(tracks.map((_, i) => i));
  useEffect(() => { order.current = shuffle ? [...tracks.keys()].sort(() => Math.random() - 0.5) : tracks.map((_, i) => i); }, [shuffle, tracks]);
  const cur = idx >= 0 ? tracks[idx] : null;
  const load = (i, autoplay = true) => {
    const t = tracks[i]; if (!t) return;
    setIdx(i); setProgress(0);
    const a = audio.current; a.src = `${base}${t.audio_url}`;
    if (autoplay) a.play().then(() => { setPlaying(true); if (!started.current) { started.current = true; onStart?.(); } }).catch(() => setPlaying(false));
  };
  const step = (d) => { const o = order.current; const pos = o.indexOf(idx); const next = o[(pos + d + o.length) % o.length]; load(next); };
  const toggle = () => { if (idx < 0) return load(order.current[0]); const a = audio.current; if (a.paused) a.play().then(() => setPlaying(true)).catch(() => {}); else { a.pause(); setPlaying(false); } };
  useEffect(() => {
    const a = audio.current;
    const onTime = () => setProgress(a.duration ? a.currentTime / a.duration : 0);
    const onEnd = () => { const o = order.current; const pos = o.indexOf(idx); if (pos < o.length - 1) load(o[pos + 1]); else { setPlaying(false); setProgress(0); } };
    a.addEventListener("timeupdate", onTime); a.addEventListener("ended", onEnd);
    return () => { a.removeEventListener("timeupdate", onTime); a.removeEventListener("ended", onEnd); };
  }, [idx]);
  useEffect(() => () => { audio.current?.pause(); }, []);
  if (!tracks.length) return null;
  return (
    <>
      <audio ref={audio} preload="none" data-testid="artist-playlist-audio" />
      <button type="button" onClick={toggle} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold glow-primary hover:opacity-90 transition-opacity" data-testid="artist-play-all">
        {playing ? <Pause size={15} /> : <Play size={15} />} {playing ? "Pause" : idx >= 0 ? "Resume" : `Play all · ${tracks.length}`}
      </button>
      {cur && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/90 backdrop-blur-xl" data-testid="artist-playlist-bar">
          <div className="h-0.5 bg-muted"><div className="h-0.5 bg-primary transition-[width]" style={{ width: `${progress * 100}%` }} /></div>
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
            <img src={`${base}${cur.cover_url}`} alt="" className="w-11 h-11 rounded-lg object-cover border border-border shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate" data-testid="artist-playlist-title">{cur.title}</div>
              <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5"><ListMusic size={11} /> {name} · track {order.current.indexOf(idx) + 1}/{tracks.length}{cur.genre ? ` · ${cur.genre}` : ""}</div>
            </div>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => setShuffle((s) => !s)} aria-pressed={shuffle} title="Shuffle" className={`h-9 w-9 inline-flex items-center justify-center rounded-full border ${shuffle ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid="artist-playlist-shuffle"><Shuffle size={14} /></button>
              <button type="button" onClick={() => step(-1)} className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground" data-testid="artist-playlist-prev"><SkipBack size={14} /></button>
              <button type="button" onClick={toggle} className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground" data-testid="artist-playlist-toggle">{playing ? <Pause size={16} /> : <Play size={16} />}</button>
              <button type="button" onClick={() => step(1)} className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground" data-testid="artist-playlist-next"><SkipForward size={14} /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
